import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import {
  useCurrencySettingsStore,
  formatCurrency,
} from '../stores/currencySettingsStore';

// shadcn/ui components
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Lucide React icons
import { DollarSign, RefreshCw, Info } from 'lucide-react';

const CurrencySettings: React.FC = () => {
  const { translate } = useTranslation();
  const { settings, setSettings, resetToDefault, loadSettings, isLoading, isSaving, hasLoadedFromAPI } = useCurrencySettingsStore();
  const [previewAmount, setPreviewAmount] = useState(1234567.89);

  // Load settings from API on mount
  React.useEffect(() => {
    if (!hasLoadedFromAPI) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSettingChange = async (key: keyof typeof settings, value: any) => {
    await setSettings({ [key]: value });
  };

  const handleReset = async () => {
    await resetToDefault();
  };

  const commonCurrencies = [
    { symbol: '₫', text: 'VND', precision: 0, position: 'after' as const },
    { symbol: '$', text: 'USD', precision: 2, position: 'before' as const },
    { symbol: '€', text: 'EUR', precision: 2, position: 'before' as const },
    { symbol: '£', text: 'GBP', precision: 2, position: 'before' as const },
    { symbol: '¥', text: 'JPY', precision: 0, position: 'before' as const },
    { symbol: '₩', text: 'KRW', precision: 0, position: 'before' as const },
  ];

  const handleCurrencyPreset = async (preset: (typeof commonCurrencies)[0]) => {
    await setSettings({
      symbol: preset.symbol,
      text: preset.text,
      precision: preset.precision,
      position: preset.position,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DollarSign className="h-6 w-6 text-primary" />
            <div>
              <CardTitle>{translate('pos.settings.currency.title')}</CardTitle>
              <CardDescription className="mt-1">
                {translate('pos.settings.currency.subtitle')}
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset} disabled={isSaving || isLoading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {isSaving ? translate('pos.settings.currency.saving', 'Saving...') : translate('pos.settings.currency.reset')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Currency Presets */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {translate('pos.settings.currency.presets')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {commonCurrencies.map((currency) => (
              <Button
                key={currency.text}
                variant="outline"
                size="sm"
                onClick={() => handleCurrencyPreset(currency)}
              >
                {currency.symbol} {currency.text}
              </Button>
            ))}
          </div>
        </div>

        <Separator />

        {/* Custom Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {translate('pos.settings.currency.custom')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Currency Symbol */}
            <div className="space-y-2">
              <Label htmlFor="currency-symbol">
                {translate('pos.settings.currency.symbol')}
              </Label>
              <Input
                id="currency-symbol"
                value={settings.symbol}
                onChange={(e) => handleSettingChange('symbol', e.target.value)}
                disabled={isLoading || isSaving}
              />
              <p className="text-xs text-muted-foreground">
                {translate('pos.settings.currency.symbolHelp')}
              </p>
            </div>

            {/* Currency Code */}
            <div className="space-y-2">
              <Label htmlFor="currency-code">
                {translate('pos.settings.currency.code')}
              </Label>
              <Input
                id="currency-code"
                value={settings.text}
                onChange={(e) => handleSettingChange('text', e.target.value)}
                disabled={isLoading || isSaving}
              />
              <p className="text-xs text-muted-foreground">
                {translate('pos.settings.currency.codeHelp')}
              </p>
            </div>

            {/* Decimal Places */}
            <div className="space-y-2">
              <Label htmlFor="currency-precision">
                {translate('pos.settings.currency.precision')}
              </Label>
              <Input
                id="currency-precision"
                type="number"
                min="0"
                max="4"
                value={settings.precision}
                onChange={(e) =>
                  handleSettingChange(
                    'precision',
                    parseInt(e.target.value) || 0
                  )
                }
                disabled={isLoading || isSaving}
              />
              <p className="text-xs text-muted-foreground">
                {translate('pos.settings.currency.precisionHelp')}
              </p>
            </div>

            {/* Symbol Position */}
            <div className="space-y-2">
              <Label htmlFor="currency-position">
                {translate('pos.settings.currency.position')}
              </Label>
              <Select
                value={settings.position}
                onValueChange={(value) =>
                  handleSettingChange('position', value)
                }
                disabled={isLoading || isSaving}
              >
                <SelectTrigger id="currency-position">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">
                    {translate('pos.settings.currency.positionBefore')}
                  </SelectItem>
                  <SelectItem value="after">
                    {translate('pos.settings.currency.positionAfter')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Thousands Separator */}
            <div className="space-y-2">
              <Label htmlFor="currency-thousands-separator">
                {translate('pos.settings.currency.thousandsSeparator')}
              </Label>
              <Input
                id="currency-thousands-separator"
                value={settings.thousandsSeparator}
                onChange={(e) =>
                  handleSettingChange('thousandsSeparator', e.target.value)
                }
                maxLength={1}
                disabled={isLoading || isSaving}
              />
              <p className="text-xs text-muted-foreground">
                {translate('pos.settings.currency.thousandsSeparatorHelp')}
              </p>
            </div>

            {/* Decimal Separator */}
            <div className="space-y-2">
              <Label htmlFor="currency-decimal-separator">
                {translate('pos.settings.currency.decimalSeparator')}
              </Label>
              <Input
                id="currency-decimal-separator"
                value={settings.decimalSeparator}
                onChange={(e) =>
                  handleSettingChange('decimalSeparator', e.target.value)
                }
                maxLength={1}
                disabled={isLoading || isSaving}
              />
              <p className="text-xs text-muted-foreground">
                {translate('pos.settings.currency.decimalSeparatorHelp')}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Preview */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {translate('pos.settings.currency.preview')}
          </h3>
          <div className="flex items-center gap-4">
            <div className="space-y-2 w-48">
              <Label htmlFor="preview-amount">
                {translate('pos.settings.currency.previewAmount')}
              </Label>
              <Input
                id="preview-amount"
                type="number"
                value={previewAmount}
                onChange={(e) =>
                  setPreviewAmount(parseFloat(e.target.value) || 0)
                }
              />
            </div>
            <div className="flex-1">
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(previewAmount)}
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {translate('pos.settings.currency.previewDescription')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrencySettings;
